import React from "react";
import { Combobox } from "components/ui/combobox";
import { MultiSelectCombobox } from "components/ui/multi-select-combobox";
import { format } from "date-fns";
import { Search, Download, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Calendar } from "components/ui/calendar";
import { Skeleton } from "components/ui/skeleton";
import { cn } from "lib/utils";

export interface Level {
    id: number | string;
    name: string;
    visible?: boolean;
    selected?: string[] | null;
    options?: Array<{ ou_code: string; ou_name: string }>;
    locked?: string[] | null;
}

interface Props {
    year: string;
    years?: string[];
    month: number;
    months: { value: number; label: string }[];
    day: number;
    days: number[];
    levels: Level[];
    loading: boolean;
    search: string;
    fromDate: string;
    toDate: string;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
    onSearchChange: (val: string) => void;
    onFromDateChange: (val: string) => void;
    onToDateChange: (val: string) => void;
    onYearChange?: (year: string) => void;
    onMonthChange?: (month: number) => void;
    onDayChange?: (day: number) => void;
    onSelectChange: (levelId: number | string, value: string[]) => void;
    onDownloadExcel: () => void;
    downloadingExcel?: boolean;
}

const LOCKED_MULTI_VALUE = "__locked_multiple__";

const toDateValue = (value?: string) => {
    if (!value) return undefined;
    return new Date(`${value}T00:00:00`);
};

const toApiDate = (value?: Date) => {
    if (!value) return "";
    return format(value, "yyyy-MM-dd");
};

const UnbilledDropdowns: React.FC<Props> = ({
    year,
    years,
    month,
    months,
    day,
    days,
    levels,
    loading,
    search,
    fromDate,
    toDate,
    conversionType,
    onConversionChange,
    onSearchChange,
    onFromDateChange,
    onToDateChange,
    onYearChange,
    onMonthChange,
    onDayChange,
    onSelectChange,
    onDownloadExcel,
    downloadingExcel = false,
}) => {

    const [dateOpen, setDateOpen] = React.useState<boolean>(false);
    const [dateTarget, setDateTarget] = React.useState<"from" | "to">("from");

    const date = React.useMemo(() => ({
        from: toDateValue(fromDate),
        to: toDateValue(toDate),
    }), [fromDate, toDate]);

    const handleDateSelect = (selectedDate?: Date) => {
        if (!selectedDate) return;

        if (dateTarget === "from") {
            onFromDateChange(toApiDate(selectedDate));
            if (date.to && selectedDate > date.to) {
                onToDateChange("");
            }
            setDateOpen(false);
            return;
        }

        if (dateTarget === "to") {
            onToDateChange(toApiDate(selectedDate));
            setDateOpen(false);
        }
    };

    const visibleLevels = React.useMemo(
        () =>
            (levels || [])
                .filter((l) => l.visible)
                .sort((a, b) => Number(b.id) - Number(a.id)),
        [levels]
    );

    return (
        <div className="flex flex-col gap-4 w-full">
            <div
                className="grid gap-4 items-start w-full"
                style={{ gridTemplateColumns: "140px 130px 90px repeat(auto-fit, minmax(140px, 1fr))" }}
            >
                <div className="min-w-0">
                    <label className="block mb-1 text-sm font-medium text-gray-700">Year</label>
                    <Combobox
                        className="h-10 text-sm"
                        options={(years || []).map((y) => ({
                            value: y,
                            label: y,
                        }))}
                        value={year}
                        onValueChange={(val) => val && onYearChange?.(val)}
                        disabled={loading}
                    />
                </div>

                <div className="min-w-0">
                    <label className="block mb-1 text-sm font-medium text-gray-700">Month</label>
                    <Combobox
                        className="h-10 text-sm"
                        options={months.map((m) => ({
                            value: String(m.value),
                            label: m.label,
                        }))}
                        value={String(month)}
                        onValueChange={(val) =>
                            val && onMonthChange?.(Number(val))
                        }
                        disabled={loading}
                    />
                </div>

                <div className="min-w-0">
                    <label className="block mb-1 text-sm font-medium text-gray-700">Day</label>
                    <Combobox
                        className="h-10 text-sm"
                        options={days.map((d) => ({
                            value: String(d),
                            label: String(d),
                        }))}
                        value={String(day)}
                        onValueChange={(val) =>
                            val && onDayChange?.(Number(val))
                        }
                        disabled={loading}
                    />
                </div>

                {!levels || levels.length === 0 ? (
                    Array.from({ length: 4 }).map((_, index) => (
                        <div className="min-w-0" key={index}>
                            <label className="block text-sm font-medium mb-1 text-gray-700 relative">
                                <span className="invisible">Placeholder</span>
                                <Skeleton className="h-4 w-20 absolute left-0 top-1/2 -translate-y-1/2" />
                            </label>
                            <Skeleton className="h-10 w-full" />
                        </div>
                    ))
                ) : (
                    visibleLevels.map((level) => {
                        const baseOpts =
                            level.options?.map((o) => ({
                                value: o.ou_code,
                                label: o.ou_name,
                            })) || [];

                        const lockedCodes = Array.isArray(level.locked) ? level.locked : [];
                        const isLockedLevel = lockedCodes.length > 0;
                        const hasMultipleLocked = lockedCodes.length > 1;

                        const opts =
                            Number(level.id) !== 1 && isLockedLevel && hasMultipleLocked
                                ? [
                                      ...baseOpts,
                                      {
                                          value: LOCKED_MULTI_VALUE,
                                          label: `${lockedCodes.length} selected`,
                                      },
                                  ]
                                : baseOpts;

                        return (
                            <div key={String(level.id)} className="min-w-0">
                                <label className="block text-sm font-medium mb-1 text-gray-700">
                                    {level.name}
                                </label>

                                {Number(level.id) === 1 ? (
                                    <MultiSelectCombobox
                                        options={baseOpts}
                                        value={isLockedLevel ? lockedCodes : Array.isArray(level.selected) ? level.selected : []}
                                        disabled={loading || isLockedLevel}
                                        onValueChange={(val) => {
                                            if (isLockedLevel) return;
                                            onSelectChange(level.id, val);
                                        }}
                                        searchPlaceholder={`Search ${level.name.toLowerCase()}...`}
                                        emptyText={`No ${level.name.toLowerCase()} found.`}
                                        buttonClassName="h-10 px-2.5 text-sm"
                                    />
                                ) : (
                                    <Combobox
                                        className="h-10 text-sm"
                                        options={opts}
                                        value={isLockedLevel ? hasMultipleLocked ? LOCKED_MULTI_VALUE : lockedCodes[0] ?? "" : Array.isArray(level.selected) && level.selected.length > 0 ? level.selected[0] : ""}
                                        disabled={loading || isLockedLevel}
                                        onValueChange={(val) => {
                                            if (isLockedLevel) return;
                                            onSelectChange(level.id, val ? [val] : []);
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <div className="flex items-end justify-between w-full gap-4">
                <div className="flex items-end gap-3 min-w-0">
                    <div className="min-w-0 w-[280px]">
                        <label className="block text-sm font-medium mb-1 text-gray-700">Search Customer</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => onSearchChange(e.target.value)}
                                placeholder="Search by customer name..."
                                className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                    </div>

                    <div className="min-w-0">
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                            CN Date Range
                        </label>

                        <div className="flex items-center border rounded-md bg-background h-10">
                            <Popover open={dateOpen} onOpenChange={setDateOpen}>
                                <div className="flex divide-x">
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            onClick={() => {
                                                setDateTarget("from");
                                                setDateOpen(true);
                                            }}
                                            className={cn(
                                                "h-10 px-2 py-1 justify-start text-left font-normal rounded-none rounded-l-md hover:bg-muted/50 text-xs min-w-[145px]",
                                                !date?.from && "text-gray-500"
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />
                                            <div className="flex flex-col min-w-0 leading-tight">
                                                <span className="text-xs font-normal text-gray-600">From Date</span>
                                                <span className="text-xs truncate">{date?.from ? format(date.from, "dd MMM yyyy") : "Start"}</span>
                                            </div>
                                        </Button>
                                    </PopoverTrigger>

                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            onClick={() => {
                                                setDateTarget("to");
                                                setDateOpen(true);
                                            }}
                                            className={cn(
                                                "h-10 px-2 py-1 justify-start text-left font-normal rounded-none rounded-r-md hover:bg-muted/50 text-xs min-w-[145px]",
                                                !date?.to && "text-gray-500"
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />
                                            <div className="flex flex-col min-w-0 leading-tight">
                                                <span className="text-xs font-normal text-gray-600">To Date</span>
                                                <span className="text-xs truncate">{date?.to ? format(date.to, "dd MMM yyyy") : "Latest"}</span>
                                            </div>
                                        </Button>
                                    </PopoverTrigger>
                                </div>

                                <PopoverContent className="w-auto p-0" align="start">
                                    <div className="p-4">
                                        <div className="text-sm font-semibold mb-3 text-gray-900">{dateTarget === "from" ? "Select start date" : "Select end date"}</div>

                                        <Calendar
                                            mode="single"
                                            selected={dateTarget === "from" ? date.from : date.to}
                                            onSelect={handleDateSelect}
                                            numberOfMonths={2}
                                            disabled={(d) => {
                                                if (dateTarget === "to" && date.from) {
                                                    return d < date.from;
                                                }
                                                return false;
                                            }}
                                        />

                                        <div className="flex justify-between mt-4 pt-3 border-t">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => {
                                                    onFromDateChange("");
                                                    onToDateChange("");
                                                }}
                                            >
                                                Clear
                                            </Button>

                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setDateOpen(false);
                                                }}
                                            >
                                                Close
                                            </Button>
                                        </div>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>
                </div>

                <div className="flex items-end gap-2 shrink-0 pb-[2px]">
                    {(['INR', 'Lakhs', 'Crores'] as const).map((t) => (
                        <button
                            key={t}
                            onClick={() => onConversionChange(t)}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === t ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                        >
                            {t}
                        </button>
                    ))}
                    
                    <button
                        className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onDownloadExcel}
                        disabled={downloadingExcel}
                    >
                        <Download size={16} />
                        {downloadingExcel ? 'Downloading...' : 'Excel'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnbilledDropdowns;